import React from "react";
import { Link } from "react-router-dom";
import { CloudSun, MapPin, ShieldCheck, Waves, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";

const faq = [
  {
    question: "What is the best weather forecast for sailors?",
    answer: "The best forecast for sailors combines wind speed, gusts, wind direction, waves, swell, visibility, tides and local marine warnings, not just temperature and rain.",
  },
  {
    question: "How should I read a marine weather forecast before sailing?",
    answer: "Start with wind and gust spread, then check wave height, swell period, visibility, tide timing and whether conditions are improving or getting worse during your route.",
  },
  {
    question: "Is a normal weather forecast enough for boating?",
    answer: "A normal forecast helps, but boaters also need sea-state details like waves, swell, wind shifts, tide windows and marina departure conditions.",
  },
  {
    question: "What wind forecast is safe for sailing?",
    answer: "Safe wind depends on boat size, crew skill, route exposure and sea state. Marstick helps you compare wind, gusts and waves before making a go/no-go decision.",
  },
];

const features = [
  { icon: Wind, title: "Wind and gust forecast", text: "See the wind details that affect sail plan, comfort, docking and route exposure." },
  { icon: Waves, title: "Wave and swell context", text: "Check wave height, swell direction and sea-state signals before you leave the marina." },
  { icon: ShieldCheck, title: "Go/no-go guidance", text: "Turn forecast data into a clearer safety decision for your boat and crew." },
  { icon: MapPin, title: "Local marine conditions", text: "Use location-aware weather for nearby marinas, coastal routes and sailing destinations." },
];

export default function WeatherForecastLandingContent({ lang = "en" }) {
  const prefix = `/${lang}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <section className="mt-6 space-y-5">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="rounded-[1.5rem] border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-4 shadow-lg shadow-primary/10 md:p-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-primary">
          <CloudSun className="h-4 w-4" /> Weather forecast for sailors
        </div>
        <h2 className="mt-3 max-w-4xl font-heading text-2xl font-black tracking-tight md:text-4xl">
          Weather forecast built for sailing, boating and coastal decisions
        </h2>
        <p className="mt-3 max-w-3xl text-base leading-7 text-muted-foreground md:text-lg">
          Marstick helps you read the weather forecast the way sailors need it: wind, gusts, waves, swell, visibility, tides and practical safety context before you leave.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button asChild><Link to={`${prefix}/weather`}>Open marine weather</Link></Button>
          <Button asChild variant="outline"><Link to={`${prefix}/weather/wind-guide`}>Wind guide</Link></Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <article key={feature.title} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <Icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-base font-black">{feature.title}</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{feature.text}</p>
            </article>
          );
        })}
      </div>

      <div className="rounded-[1.5rem] border border-border bg-card p-4 md:p-6">
        <h2 className="font-heading text-xl font-black md:text-2xl">Weather forecast FAQ</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {faq.map((item) => (
            <article key={item.question} className="rounded-2xl border border-border bg-muted/30 p-4">
              <h3 className="font-black">{item.question}</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="rounded-[1.5rem] border border-border bg-card p-4 md:p-6">
        <h2 className="font-heading text-xl font-black md:text-2xl">Related forecast pages</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          {[
            ["Marine weather forecast", `${prefix}/weather`],
            ["Weather guide for sailing", `${prefix}/weather/sailing-decision-guide`],
            ["Wind guide for boating", `${prefix}/weather/wind-guide`],
            ["Sea state guide", `${prefix}/weather/sea-state-guide`],
          ].map(([label, href]) => (
            <Button key={href} asChild variant="outline" size="sm"><Link to={href}>{label}</Link></Button>
          ))}
        </div>
      </div>
    </section>
  );
}